/**
 * ProductShareModal.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Web port of the mobile premium share experience for a single product.
 *
 * Shows a live preview of the branded poster, then lets the user:
 *   • "Share poster" → snapshots the poster to a high-res PNG and shares the
 *                      actual image via the Web Share API (ideal for WhatsApp
 *                      Status & chats — visual-first). On desktop browsers that
 *                      can't share files, it downloads the PNG instead.
 *   • "Send link only" → the classic text + URL share (clickable link) as a
 *                        fallback for buyers who prefer a tappable link.
 *
 * The poster image is self-contained (photo, price, vendor, QR + URL), so it
 * sells even as a Status screenshot where links aren't tappable.
 */
import React, { useCallback, useRef, useState } from "react";
import styled from "styled-components";
import { toPng } from "html-to-image";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import LinkIcon from "@mui/icons-material/Link";
import { toast } from "react-toastify";
import ShareableProductPoster, { POSTER_W } from "./ShareableProductPoster";
import { buildProductShareText } from "../SEO/vendorSeo";

const ACCENT = "#2d3e50";

/* The caption is the same product share text the shop pages compose, so the
   poster and the plain-text share never drift, and it stays emoji-free (the
   wa.me deep link mangles emoji on the desktop client; see vendorSeo). */
const buildCaption = (item, shopUrl) =>
  buildProductShareText(
    { name: item?.name || "Featured product", price: item?.price },
    { url: shopUrl, vendor: item?.vendorName ? { name: item.vendorName } : undefined }
  );

const slugifyFilename = (name) =>
  (name || "lusuku-product")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 50) || "lusuku-product";

const ProductShareModal = ({ visible, item, shopUrl, onClose }) => {
  const posterRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const capturePng = useCallback(async () => {
    const node = posterRef.current;
    if (!node) throw new Error("Poster not ready");
    // pixelRatio 3 upscales the 360px-wide poster to ~1080px for crisp Status
    // posting. cacheBust avoids stale cross-origin image caching during inline.
    return toPng(node, {
      pixelRatio: 3,
      cacheBust: true,
      backgroundColor: "#1B2F34",
    });
  }, []);

  const handleSharePoster = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const dataUrl = await capturePng();
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `${slugifyFilename(item?.name)}.png`, {
        type: "image/png",
      });

      // Prefer sharing the actual image file (mobile browsers, some desktops).
      if (
        navigator.canShare &&
        navigator.canShare({ files: [file] }) &&
        navigator.share
      ) {
        await navigator.share({
          files: [file],
          title: item?.name || "Lusuku product",
          text: buildCaption(item, shopUrl),
        });
      } else {
        // Fallback: download the poster so the user can attach it manually.
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Poster downloaded — ready to share!");
      }
    } catch (err) {
      // AbortError = user cancelled the native share sheet; ignore it.
      if (err && err.name === "AbortError") return;
      console.error("Poster share failed:", err);
      toast.error("Couldn't generate the poster. Try 'Send link only'.");
    } finally {
      setBusy(false);
    }
  }, [busy, capturePng, item, shopUrl]);

  const handleShareLink = useCallback(async () => {
    const text = buildCaption(item, shopUrl);
    try {
      if (navigator.share) {
        await navigator.share({
          title: item?.name || "Lusuku product",
          text,
          url: shopUrl,
        });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Link copied to clipboard!");
      }
    } catch (err) {
      if (err && err.name === "AbortError") return;
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Link copied to clipboard!");
      } catch (_) {
        toast.error("Couldn't share the link.");
      }
    }
  }, [item, shopUrl]);

  if (!visible) return null;

  return (
    <Overlay onClick={onClose}>
      <Sheet onClick={(e) => e.stopPropagation()}>
        <SheetHeader>
          <SheetTitle>Share product</SheetTitle>
          <CloseBtn onClick={onClose} aria-label="Close">
            <CloseIcon />
          </CloseBtn>
        </SheetHeader>

        <PosterFrame>
          {item ? (
            <ShareableProductPoster
              ref={posterRef}
              item={item}
              shopUrl={shopUrl}
            />
          ) : null}
        </PosterFrame>

        <Actions>
          <PrimaryBtn onClick={handleSharePoster} disabled={busy}>
            <ImageOutlinedIcon style={{ fontSize: 18 }} />
            <span>{busy ? "Preparing…" : "Share poster"}</span>
          </PrimaryBtn>
          <SecondaryBtn onClick={handleShareLink}>
            <LinkIcon style={{ fontSize: 16 }} />
            <span>Send link only</span>
          </SecondaryBtn>
        </Actions>
      </Sheet>
    </Overlay>
  );
};

export default ProductShareModal;

/* ─── Styles ──────────────────────────────────────────────────────────────── */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 400px;
  background: #ffffff;
  border-radius: 24px;
  padding: 16px 18px 18px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
`;

const SheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const SheetTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: #16252a;
`;

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: #16252a;
  display: flex;
  padding: 4px;
  border-radius: 50%;
  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
`;

const PosterFrame = styled.div`
  width: ${POSTER_W}px;
  max-width: 100%;
  margin: 0 auto;
  border-radius: 18px;
  overflow: hidden;
`;

const Actions = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PrimaryBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${ACCENT};
  color: #ffffff;
  font-size: 16px;
  font-weight: 800;
  border: none;
  border-radius: 14px;
  padding: 14px;
  cursor: pointer;
  transition: opacity 0.15s ease;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const SecondaryBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: transparent;
  color: ${ACCENT};
  font-size: 14px;
  font-weight: 700;
  border: none;
  padding: 10px;
  cursor: pointer;
`;
